const AUTH_TOKEN_KEY = 'foodbook:self-hosted:token';
export const AUTH_INVALID_EVENT = 'foodbook:auth-invalid';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function getApiBaseUrl(): string {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      const configuredBaseUrl = String(import.meta.env.VITE_API_BASE_URL || '').trim();
      if (configuredBaseUrl) {
        return configuredBaseUrl.replace(/\/+$/, '');
      }
    }
  } catch {
    // ignore
  }

  if (typeof window !== 'undefined') {
    const location = window.location;
    const protocol = typeof location?.protocol === 'string' ? location.protocol : '';
    const hostname = typeof location?.hostname === 'string' ? location.hostname : '';
    const origin = typeof location?.origin === 'string' ? location.origin : '';

    if (!hostname) {
      return '';
    }

    if (hostname === '127.0.0.1' || hostname === 'localhost') {
      return `${protocol || 'http:'}//${hostname}:8787`;
    }
    return origin ? origin.replace(/\/+$/, '') : '';
  }

  return '';
}

export function isApiConfigured(): boolean {
  return getApiBaseUrl().length > 0;
}

export function getStoredAuthToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function storeAuthToken(token: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function clearStoredAuthToken(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const baseUrl = getApiBaseUrl();
  if (!baseUrl && typeof window === 'undefined') {
    throw new ApiError('未配置自托管 API 地址', 0);
  }

  const headers = new Headers(init.headers || {});
  if (!headers.has('Content-Type') && init.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }

  const token = getStoredAuthToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const normalizedPath = baseUrl.endsWith('/api') && path.startsWith('/api/')
    ? path.slice('/api'.length)
    : path;

  const response = await fetch(`${baseUrl}${normalizedPath}`, {
    ...init,
    headers,
  });

  if (response.status === 401) {
    clearStoredAuthToken();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(AUTH_INVALID_EVENT));
    }
  }

  const text = await response.text();
  const payload = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const message = payload?.error || payload?.message || `请求失败: ${response.status}`;
    throw new ApiError(message, response.status, payload);
  }

  return payload as T;
}
